<template>
  <div class="lg:mr-44">
    <!-- Hero -->
    <div
      :style="[
        { backgroundImage: 'url(/img/careers-expo-9.jpg)' },
        { minHeight: '500px' },
        { backgroundPosition: '50% 20%' },
      ]"
      class="hero-gradient bg-cover relative w-full flex flex-col justify-end items-center pb-12 text-white bg-no-repeat"
    >
      <h4 class="z-20 font-poppins uppercase text-expo_red">
        Register attendance
      </h4>
      <h1 class="z-20 font-bold">School Registration</h1>
    </div>

    <!-- Content start -->
    <div
      class="bg-truegray-100 flex flex-col items-center justify-center py-24 px-2 lg:px-10"
    >
      <div
        class="w-full max-w-6xl bg-white rounded-xl shadow relative overflow-hidden"
      >
        <!-- Header -->
        <div
          class="bg-expo_dblue w-full flex flex-col lg:flex-nowrap text-center items-center justify-center space-y-8 pt-12 z-10"
        >
          <img
            class="h-16 z-10 mt-8"
            src="/img/expo_symbol_white.png"
            alt="expo logo symbol"
          />
          <h2 class="font-questrial text-white text-center z-10 px-6">
            Careers Expo School Registration
          </h2>
        </div>
        <div class="form-header_curve bg-expo_dblue w-full"></div>

        <!-- Step wrapper -->
        <div
          id="stepWrapper"
          :style="{ height: stepHeight }"
          class="relative transition-all duration-300 ease-in-out z-60"
        >
          <!-- Step 1 -->
          <div
            id="step-1"
            :class="
              activeStep === 1 ? 'opacity-100' : 'pointer-events-none opacity-0'
            "
            class="absolute inset-0 p-12 2xl:p-24 transition-all duration-300 ease-in-out delay-100"
          >
            <div class="w-full pb-4 border-b border-truegray-300">
              <h5 class="text-sm m-0 font-black text-truegray-500">
                Step {{ activeStep }}
              </h5>
              <h4 class="text-expo_dblue">What are you interested in?</h4>
            </div>
            <div
              class="w-full grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-12 mt-12"
            >
              <div class="w-full">
                <h4 class="text-expo_dblue">Attend an Expo</h4>
                <h5 class="text-sm mb-6 font-black text-truegray-400">
                  Attend one of our yearly physical Expos
                </h5>
                <h5 class="text-sm font-black text-truegray-500">
                  By letting us know when you plan to bring your students along
                  to one of our events, it gives us the chance to ensure you
                  have the best experience.
                </h5>

                <div class="flex pt-16 items-center justify-start">
                  <button
                    class="btn btn-expo-red hover:text-expo-red no-underline w-48 h-8"
                    @click="changeStep()"
                  >
                    Register
                  </button>
                </div>
              </div>
              <div class="w-full">
                <img
                  class="h-16 pb-2"
                  src="/img/cw-logo.png"
                  alt="CareerWise logo"
                />
                <h5 class="text-sm mb-6 font-black text-truegray-400">
                  Take your careers department online with CareerWise
                </h5>
                <h5 class="text-sm font-black text-truegray-500">
                  To help support our growing online presence, we've partnered
                  with Careerwise, An online platform thoughtfully designed for
                  career advisors to connect, engage with their students and
                  access relevant information from the Career Wise cloud.
                </h5>
                <a
                  ref="CWLink"
                  class="hidden"
                  :href="settings.central_domain_url"
                ></a>
                <div class="flex pt-16 items-center justify-start">
                  <button
                    class="btn btn-expo-red hover:text-expo-red no-underline w-48 h-8"
                    @click="goToCW"
                  >
                    Learn more
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div
            id="step-2"
            :class="
              activeStep === 2 ? 'opacity-100' : 'pointer-events-none opacity-0'
            "
            class="absolute inset-0 p-12 2xl:p-24 transition-all duration-300 ease-in-out delay-100"
          >
            <div class="w-full pb-4 border-b border-truegray-300">
              <h5 class="text-sm m-0 font-black text-truegray-500">
                Step {{ activeStep }}
              </h5>
              <h4 class="text-expo_dblue">Booking Details</h4>
            </div>
            <div
              class="grid grid-cols-1 lg:grid-cols-4 gap-x-24 gap-y-8 py-8 border-b border-truegray-300"
            >
              <div v-for="(event, i) in storeEvents" :key="i" class="relative">
                <p>{{ event.title }}</p>
                <h5 class="text-sm mb-2 font-black text-expo_red">
                  {{ event.date_range }}
                </h5>
                <h5
                  class="cursor-pointer text-sm mb-2 font-black text-truegray-400 underline italic"
                  @click="toggleScheduleModal(i)"
                >
                  View schedule
                </h5>
              </div>
            </div>

            <!-- Expo details -->
            <h5 class="pt-8">Expo Details:</h5>
            <div
              class="grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-8 py-8 border-b border-truegray-300 dj-datepicker"
            >
              <div>
                <p class="pb-2">
                  Which Expo are you attending?
                  <span class="text-xs text-expo_red">* </span>
                  <span class="text-truegray-500 text-xs italic">required</span>
                </p>

                <InputDropdown
                  v-if="loaded"
                  :selected-options-initial="[events.event_id]"
                  :options="eventsDropdown"
                  :margin-bottom="false"
                  :shadow-enabled="false"
                  :response-selection-id="'new_event'"
                />
              </div>

              <div>
                <p class="pb-2">
                  What date will you be attending?
                  <span class="text-xs text-expo_red">* </span>
                  <span class="text-truegray-500 text-xs italic">required</span>
                </p>
                <date-picker
                  v-model="events.date"
                  value="datetime"
                  value-type="format"
                  format="DD-MM-YYYY"
                  @change="validateForm()"
                  @keyup="validateForm()"
                ></date-picker>
              </div>

              <div>
                <p class="pb-2">
                  What time are you arriving?
                  <span class="text-xs text-expo_red">* </span>
                  <span class="text-truegray-500 text-xs italic">required</span>
                </p>
                <date-picker
                  v-model="events.arrival"
                  format="hh:mm a"
                  value-type="format"
                  type="time"
                  :minute-step="15"
                  @change="validateForm()"
                  @keyup="validateForm()"
                ></date-picker>
              </div>

              <div>
                <p class="pb-2">
                  Number of students attending
                  <span class="text-xs text-expo_red">* </span>
                  <span class="text-truegray-500 text-xs italic">required</span>
                </p>
                <input
                  v-model="events.students"
                  :class="'placeholder-truegray-300 text-gray-600 focus:text-gray-600 visited:text-gray-600 focus:ring-gray-600'"
                  class="flex-grow px-4 mr-0 mt-1 outline-none focus:outline-none bg-transparent w-full h-12 font-primary text-black focus:placeholder-truegray-300 rounded-lg relative bg-white border border-truegray-300 mb-4"
                  type="number"
                  min="0"
                  autocomplete="off"
                  @change="validateForm()"
                  @keyup="validateForm()"
                />
              </div>

              <div>
                <p class="pb-2">
                  Please indiciate your student year group(s)
                  <span class="text-xs text-expo_red">* </span>
                  <span class="text-truegray-500 text-xs italic">required</span>
                </p>

                <InputDropdown
                  v-if="loaded"
                  :selected-options-initial="[selected_years]"
                  :options="available_years"
                  :margin-bottom="false"
                  :shadow-enabled="false"
                  :multiselect="true"
                  :response-selection-id="'new_years'"
                />

                <p class="italic mt-2">
                  Please note: the NZ Careers Expo is ideally suited for
                  secondary school level.
                </p>
              </div>
            </div>

            <!-- School details -->
            <h5 class="pt-8">School Details:</h5>
            <div
              class="grid grid-cols-1 lg:grid-cols-3 gap-x-16 gap-y-8 pt-8 dj-datepicker"
            >
              <div>
                <p class="pb-2">
                  School Name <span class="text-xs text-expo_red">* </span>
                  <span class="text-truegray-500 text-xs italic">required</span>
                </p>
                <input
                  id="name"
                  v-model="organisation.name"
                  :class="'placeholder-truegray-300 text-gray-600 focus:text-gray-600 visited:text-gray-600 focus:ring-gray-600'"
                  class="flex-grow px-4 mr-0 mt-1 outline-none focus:outline-none bg-transparent w-full h-12 font-primary text-black focus:placeholder-truegray-300 rounded-lg relative bg-white border border-truegray-300 mb-4"
                  name="name"
                  type="text"
                  autocomplete="off"
                  @change="validateForm()"
                  @keyup="validateForm()"
                />
              </div>

              <div>
                <p class="pb-2">Website</p>
                <input
                  id="website"
                  v-model="organisation.website"
                  :class="
                    validationErr.includes('organisation_website')
                      ? 'border-red-300 text-red-600'
                      : 'border-truegray-300'
                  "
                  class="placeholder-truegray-300 focus:border-truegray-300 text-gray-600 focus:text-gray-600 visited:text-gray-600 focus:ring-gray-600 flex-grow px-4 mr-0 mt-1 outline-none focus:outline-none bg-transparent w-full h-12 font-primary focus:placeholder-truegray-300 rounded-lg relative bg-white border mb-4"
                  name="website"
                  type="text"
                  autocomplete="off"
                  @change="validateForm()"
                  @keyup="validateForm()"
                />
              </div>

              <div>
                <p class="pb-2">Post Code</p>
                <input
                  id="code"
                  v-model="organisation.postcode"
                  :class="'placeholder-truegray-300 text-gray-600 focus:text-gray-600 visited:text-gray-600 focus:ring-gray-600'"
                  class="flex-grow px-4 mr-0 mt-1 outline-none focus:outline-none bg-transparent w-full h-12 font-primary text-black focus:placeholder-truegray-300 rounded-lg relative bg-white border border-truegray-300 mb-4"
                  name="code"
                  type="text"
                  autocomplete="off"
                  @change="validateForm()"
                  @keyup="validateForm()"
                />
              </div>
            </div>
            <div class="w-full pb-8 border-b border-truegray-300">
              <p class="pb-2">
                Address <span class="text-xs text-expo_red">* </span>
                <span class="text-truegray-500 text-xs italic">required</span>
              </p>
              <textarea
                id="description"
                v-model="organisation.address"
                name="description"
                rows="2"
                class="flex-grow p-4 mr-0 mt-1 outline-none focus:outline-none bg-transparent w-full font-primary text-black focus:placeholder-truegray-300 rounded-lg relative bg-white border border-truegray-300 mb-4 placeholder-truegray-300 text-gray-600 focus:text-gray-600 visited:text-gray-600 focus:ring-gray-600"
                @change="validateForm()"
              ></textarea>
            </div>

            <!-- Key contact -->
            <h5 class="pt-8">Key Contact Details:</h5>
            <p class="text-sm text-truegray-500 italic">
              Key Contacts will be added to our Schools (email) mailing list for
              important updates and announcements.
            </p>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-8 pt-8">
              <div>
                <p class="pb-2">
                  Name <span class="text-xs text-expo_red">* </span>
                  <span class="text-truegray-500 text-xs italic">required</span>
                </p>
                <input
                  id="name"
                  v-model="contact.name"
                  :class="'placeholder-truegray-300 text-gray-600 focus:text-gray-600 visited:text-gray-600 focus:ring-gray-600'"
                  class="flex-grow px-4 mr-0 mt-1 outline-none focus:outline-none bg-transparent w-full h-12 font-primary text-black focus:placeholder-truegray-300 rounded-lg relative bg-white border border-truegray-300 mb-4"
                  name="name"
                  type="text"
                  autocomplete="off"
                  @change="validateForm()"
                  @keyup="validateForm()"
                />
              </div>

              <div>
                <p class="pb-2">Phone</p>
                <input
                  id="contactphone"
                  v-model="contact.phone"
                  :class="
                    validationErr.includes('contact_phone')
                      ? 'border-red-300 text-red-600'
                      : 'border-truegray-300'
                  "
                  class="placeholder-truegray-300 focus:border-truegray-300 text-gray-600 focus:text-gray-600 visited:text-gray-600 focus:ring-gray-600 flex-grow px-4 mr-0 mt-1 outline-none focus:outline-none bg-transparent w-full h-12 font-primary focus:placeholder-truegray-300 rounded-lg relative bg-white border mb-4"
                  name="contactphone"
                  type="tel"
                  autocomplete="off"
                  @change="validateForm()"
                  @keyup="validateForm()"
                />
              </div>

              <div>
                <p class="pb-2">
                  Mobile <span class="text-xs text-expo_red">* </span>
                  <span class="text-truegray-500 text-xs italic">required</span>
                </p>
                <input
                  id="contactMobile"
                  v-model="contact.mobile"
                  :class="
                    validationErr.includes('contact_mobile')
                      ? 'border-red-300 text-red-600'
                      : 'border-truegray-300'
                  "
                  class="placeholder-truegray-300 focus:border-truegray-300 text-gray-600 focus:text-gray-600 visited:text-gray-600 focus:ring-gray-600 flex-grow px-4 mr-0 mt-1 outline-none focus:outline-none bg-transparent w-full h-12 font-primary focus:placeholder-truegray-300 rounded-lg relative bg-white border mb-4"
                  name="contactMobile"
                  type="tel"
                  autocomplete="off"
                  @change="validateForm()"
                  @keyup="validateForm()"
                />
              </div>

              <div>
                <p class="pb-2">
                  Email <span class="text-xs text-expo_red">* </span>
                  <span class="text-truegray-500 text-xs italic">required</span>
                </p>
                <input
                  id="contactEmail"
                  v-model="contact.email"
                  :class="
                    validationErr.includes('contact_email')
                      ? 'border-red-300 text-red-600'
                      : 'border-truegray-300'
                  "
                  class="placeholder-truegray-300 focus:border-truegray-300 text-gray-600 focus:text-gray-600 visited:text-gray-600 focus:ring-gray-600 flex-grow px-4 mr-0 mt-1 outline-none focus:outline-none bg-transparent w-full h-12 font-primary focus:placeholder-truegray-300 rounded-lg relative bg-white border mb-4"
                  name="contactEmail"
                  type="email"
                  autocomplete="off"
                  @change="validateForm()"
                  @keyup="validateForm()"
                />
              </div>
            </div>
            <div class="w-full pb-8">
              <p class="pb-2">Comments</p>
              <textarea
                id="comments"
                v-model="comments"
                name="comments"
                rows="2"
                class="flex-grow p-4 mr-0 mt-1 outline-none focus:outline-none bg-transparent w-full font-primary text-black focus:placeholder-truegray-300 rounded-lg relative bg-white border border-truegray-300 mb-4 placeholder-truegray-300 text-gray-600 focus:text-gray-600 visited:text-gray-600 focus:ring-gray-600"
                @change="validateForm()"
                @keyup="validateForm()"
              ></textarea>
            </div>

            <!-- Form base -->
            <div class="relative z-10">
              <div class="flex space-x-12 w-full justify-center">
                <div class="py-8">
                  <button
                    :disabled="!canSubmit || emailSent"
                    class="btn btn-expo-red h-8 relative"
                    :class="canSubmit ? '' : 'opacity-25 cursor-not-allowed'"
                    @click="submitRegistration()"
                  >
                    <div
                      class="absolute inset-0 w-full flex justify-center items-center z-20"
                    >
                      <lottie
                        v-if="thinking"
                        :options="loadingAnimationOptions"
                      />
                    </div>
                    <span :class="[thinking ? 'invisible' : '']">Submit</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- showScheduleModal Modal -->
    <div
      v-if="showScheduleModal"
      id="showScheduleModal"
      class="fixed z-60 inset-0 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div
        class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        ></div>
        <!-- This element is to trick the browser into centering the modal contents. -->
        <span
          class="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
          >&#8203;</span
        >
        <!-- Modal panel, show/hide based on modal state. -->
        <div
          class="inline-block align-bottom bg-white rounded-lg p-6 text-left overflow-visible shadow transform transition-all mt-8 mb-20 align-middle max-w-3xl w-full lg:p-16 relative z-60"
        >
          <div class="flex flex-col space-y-4">
            <h3>{{ storeEvents[activeEvent].title }}</h3>
            <h5 class="text-sm mb-2 font-black text-expo_red">
              {{ storeEvents[activeEvent].date_range }}
            </h5>
            <div
              class="wysiwyg_html text-sm mb-6 font-black text-truegray-400"
              v-html="storeEvents[activeEvent].schedule"
            ></div>
            <button
              type="button"
              class="absolute right-8 top-6 transform hover:rotate-180 transition-all duration-300 hover:text-primary"
              @click="showScheduleModal = !showScheduleModal"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { shared } from '../../../js/mixins/shared.js'
import { EventBus } from '../../../js/plugins/event-bus.js'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import InputDropdown from '../../../shared/components/InputDropdown.vue'
import * as loadingAnimation from '../../../shared/components/loading.json'

export default {
  components: {
    InputDropdown,
    DatePicker,
  },

  mixins: [shared],

  data() {
    return {
      loaded: false,
      thinking: false,
      loadingAnimationOptions: { animationData: loadingAnimation.default },
      loadingAnimate: false,

      showScheduleModal: false,
      activeEvent: 0,
      eventsDropdown: [],

      activeStep: 1,
      stepHeight: 0,
      stepHeights: {
        1: 0,
        2: 0,
      },

      canSubmit: false,

      organisation: {
        name: '',
        website: '',
        postcode: '',
        address: '',
      },

      contact: {
        name: '',
        phone: '',
        mobile: '',
        email: '',
      },

      events: {
        event_title: '',
        event_id: null,
        date: '',
        arrival: '',
        students: 0,
      },

      selected_years: null,
      available_years: [
        { text: 'Year 9', value: 9 },
        { text: 'Year 10', value: 10 },
        { text: 'Year 11', value: 11 },
        { text: 'Year 12', value: 12 },
        { text: 'Year 13', value: 13 },
      ],

      comments: '',
      category: 1, //tell controller this is a school booking

      validationErr: [],
      emailSent: false,
    }
  },

  computed: {
    // Set all map getters
    ...mapGetters({
      settings: 'shared/getSettings',
      storeEvents: 'expo/getEvents',
    }),
  },

  watch: {
    showScheduleModal: {
      handler: function (val) {
        EventBus.$emit('modal_state_changing', val)
      },

      deep: true,
    },

    selected_years: {
      handler: function () {
        this.calcStepHeights()
        this.validateForm()
      },

      deep: true,
    },

    activeStep: {
      handler: function (val) {
        let stepArrVal = val - 1
        this.$set(this, 'stepHeight', this.stepHeights[stepArrVal] + 'px')
      },

      deep: true,
    },

    stepHeight: {
      handler: function () {},
      deep: true,
    },
  },

  created() {
    window.addEventListener('resize', this.calcStepHeights)
  },

  async mounted() {
    this.calcStepHeights()
    this.$set(this, 'stepHeight', this.stepHeights[0] + 'px')

    if (this.storeEvents.length < 1) {
      await this.dispatchSetEvents()
      // @todo test/fix/remove
      setTimeout(() => {
        this.createEventsDropdown()
        this.calcStepHeights()
        this.$set(this, 'loaded', true)
      }, 100)
    } else {
      this.calcStepHeights()
      this.createEventsDropdown()
      this.$set(this, 'loaded', true)
    }

    EventBus.$on('new_event', (selected_value) => {
      // We are passed the event id --- we need to get the correct event info...
      let event = false
      this.eventsDropdown.forEach((i) => {
        if (i.value === selected_value) {
          event = i
        }
      })
      if (event) {
        this.$set(this.events, 'event_title', event.text)
        this.$set(this.events, 'event_id', event.value)
      }
    })

    EventBus.$on('new_years', (val) => {
      let filteredArr = val.filter(Boolean)
      this.$set(this, 'selected_years', filteredArr)
    })
  },

  unmounted() {
    window.removeEventListener('resize', this.calcStepHeights)
  },

  methods: {
    ...mapActions({
      dispatchSetEvents: 'expo/dispatchSetEvents',
    }),

    // @todo validate form when event is changed - without infinite loop!
    validateForm() {
      let validForm = true
      let validationErr = []

      if (this.events.event_id < 0 || !this.events.event_title) {
        validForm = false
      }

      if (!this.events.event_title || !this.events.event_title.length > 1) {
        validForm = false
      }

      if (!this.events.date || !this.events.date.length > 1) {
        validForm = false
      }

      if (!this.events.arrival || !this.events.arrival.length > 1) {
        validForm = false
      }

      if (!this.events.students || !this.events.students.length > 1) {
        validForm = false
      }

      if (!this.contact.name || !this.contact.name.length > 1) {
        validForm = false
      }

      if (!this.organisation.name || !this.organisation.name.length > 1) {
        validForm = false
      }

      if (!this.organisation.address || !this.organisation.address.length > 1) {
        validForm = false
      }

      if (!this.contact.email || !this.contact.email.length > 1) {
        validForm = false
      }

      if (!this.selected_years || !this.selected_years.length > 0) {
        validForm = false
      }

      // validateURL
      if (
        this.organisation.website &&
        !this.validateURL(this.organisation.website)
      ) {
        validationErr.push('organisation_website')
        validForm = false
      }

      // validateEmail
      if (this.contact.email && !this.validateEmail(this.contact.email)) {
        validationErr.push('contact_email')
        validForm = false
      }

      // !this.validatePhoneNumber(this.contact.phone)
      if (this.contact.phone && this.contact.phone.length < 6) {
        validationErr.push('contact_phone')
        validForm = false
      }

      // !this.validatePhoneNumber(this.contact.mobile)
      if (this.contact.mobile && this.contact.mobile.length < 7) {
        validationErr.push('contact_mobile')
        validForm = false
      }

      this.$set(this, 'validationErr', [])
      this.$set(this, 'validationErr', validationErr)

      this.$set(this, 'canSubmit', validForm)
    },

    createEventsDropdown() {
      let arr = []
      this.storeEvents.forEach((event) => {
        let obj = {}
        obj.value = event.id
        obj.text = event.title
        arr.push(obj)
      })
      this.$set(this, 'eventsDropdown', arr)
    },

    toggleScheduleModal(i) {
      this.$set(this, 'activeEvent', i)
      this.showScheduleModal = !this.showScheduleModal
    },

    goToCW() {
      this.$refs.CWLink.click()
    },

    returnDataNodeValue(key) {
      let value = null
      switch (key) {
        // // Step 1
        case 'events':
          value = this.events
          break

        default:
          break
      }
      return value
    },

    changeStep(direction) {
      let step = null
      if (direction === '-') {
        step = this.activeStep - 1
      } else {
        step = this.activeStep + 1
      }

      this.$set(this, 'activeStep', step)

      setTimeout(() => {
        this.$set(this, 'stepHeight', this.stepHeights[step - 1] + 'px') //-1 for array
        this.$scrollTo('#stepWrapper')
      }, 300)
    },

    calcStepHeights() {
      let step1 = document.getElementById('step-1')
      let step2 = document.getElementById('step-2')
      this.stepHeights[0] = step1.scrollHeight
      this.stepHeights[1] = step2.scrollHeight

      let stepArrVal = this.activeStep - 1

      this.$set(this, 'stepHeight', this.stepHeights[stepArrVal] + 'px')
      // lol sorry
    },

    submitRegistration() {
      let submitData = {
        organisation: this.organisation,
        contact: this.contact,
        events: this.events,
        comments: this.comments,
        category: 1, // 1 = school, 0 = exhibitor
        years: this.selected_years,
      }

      this.$set(this, 'thinking', true)

      axios.post('/api/expo-registration', submitData).then((response) => {
        if (response.data.success) {
          this.$set(this, 'emailSent', true)
        } else {
          //...
        }

        this.$set(this, 'thinking', false)

        if (response.data.alert) {
          EventBus.$emit('triggerAlertResponse', response.data.alert)
        }
      })
    },
  },
}
</script>

<style scoped>
.form-header_curve {
  border-bottom-left-radius: 100% 200px;
  border-bottom-right-radius: 100% 200px;
  height: 100px;
  margin-top: -30px;
  width: 120%;
  position: relative;
  right: 10%;
  z-index: 1;
}

.hero-gradient::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1;
  height: 100%;
  background: rgb(0, 0, 0);
  background: linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.804359243697479) 0%,
    rgba(0, 0, 0, 0.5942752100840336) 36%,
    rgba(0, 0, 0, 0) 100%
  );
}

.dj-datepicker .mx-datepicker {
  width: 100%;
  box-shadow: none;
}
.dj-datepicker .mx-datepicker .mx-input {
  height: 3rem;
  width: 100%;
}
</style>
